import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { SECTION_IDS } from '../../../constants/sectionIds';
import { spacing } from '../../../theme';
import { RequestFormAdditionalInformation } from '../subsections/AdditionalInformation';
import { RacksInformation } from './subsections/RacksInformation';
import {
  isRacksPersistedProps,
  type RacksProps,
  type RacksPersistedProps,
  type RacksInitialProps,
} from './useRacks';

const RacksInitialLayout: React.FC<RacksInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
}) => (
  <View style={racksInitialStyles.container}>
    <PersistedStepHeader
      title="Racks"
      uomSlot={null}
      actionsSlot={null}
    />

    <RacksInformation
      mode="initial"
      formData={formData}
      updateFormData={updateFormData}
      requestId={requestId}
      disabled={disabled}
      isEditing={Boolean(isEditing)}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelected}
      offlineMode={offlineMode}
    />

    <RequestFormAdditionalInformation
      shell="formSection"
      titleSubtitle="Additional rack configuration notes and supporting documents."
      sectionId={SECTION_IDS.RACKS_ADDITIONAL}
      requestId={requestId}
      allowAttachments
      attachments={attachmentsBySection[SECTION_IDS.RACKS_ADDITIONAL] || []}
      onAttachFiles={() => onAttachFiles?.(SECTION_IDS.RACKS_ADDITIONAL)}
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing || !disabled}
      disabled={disabled}
      selectedFiles={selectedFiles[SECTION_IDS.RACKS_ADDITIONAL] || []}
      onFilesSelected={
        onFilesSelected
          ? files => onFilesSelected(SECTION_IDS.RACKS_ADDITIONAL, files)
          : undefined
      }
      isOffline={offlineMode}
      fieldId="rackAdditionalRackInformation"
      permissionRequestStage={String(formData.stage ?? '')}
      inputLabel="Additional Rack Configuration Information"
      placeholder="Enter additional rack information"
      value={String(formData.rackAdditionalRackInformation ?? '')}
      onChangeText={value =>
        updateFormData('rackAdditionalRackInformation', value)
      }
      maxLength={10000}
      inputUseEditableProp={false}
      inputDisabled={disabled}
      wrapFieldInRowColumn={false}
    />
  </View>
);

const RacksPersistedLayout: React.FC<RacksPersistedProps> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
}) => {
  const reqRec = request as unknown as Record<string, unknown>;

  return (
    <View style={racksPersistedStyles.container}>
      <PersistedStepHeader
        title="Racks"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={racksPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={racksPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <RacksInformation
        mode="persisted"
        request={request}
        isEditing={isEditing}
        editedData={editedData}
        updateEditedData={updateEditedData}
        showFullDetails={showFullDetails}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
      />

      <RequestFormAdditionalInformation
        shell="formSection"
        titleSubtitle="Additional rack configuration notes and supporting documents."
        sectionId={SECTION_IDS.RACKS_ADDITIONAL}
        requestId={request.id}
        allowAttachments
        attachments={attachmentsBySection[SECTION_IDS.RACKS_ADDITIONAL] || []}
        onAttachFiles={() => onAttachFiles?.(SECTION_IDS.RACKS_ADDITIONAL)}
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing}
        disabled={!isEditing}
        readOnlyMode={!isEditing}
        selectedFiles={selectedFiles[SECTION_IDS.RACKS_ADDITIONAL] || []}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(SECTION_IDS.RACKS_ADDITIONAL, files)
            : undefined
        }
        isOffline={offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        fieldId="rackAdditionalRackInformation"
        permissionRequestStage={String(
          request.stage ?? request.sysRequestStage ?? '',
        )}
        showFullDetails={showFullDetails}
        inputLabel="Additional Rack Configuration Information"
        placeholder="Enter additional rack information"
        value={
          isEditing
            ? String(
                editedData.rackAdditionalRackInformation ??
                  reqRec.rackAdditionalRackInformation ??
                  '',
              )
            : String(reqRec.rackAdditionalRackInformation ?? '')
        }
        onChangeText={
          isEditing
            ? value => updateEditedData('rackAdditionalRackInformation', value)
            : () => {}
        }
        maxLength={10000}
        inputUseEditableProp
        inputEditable={isEditing}
        wrapFieldInRowColumn={false}
      />
    </View>
  );
};

const racksInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  uomContainer: {
    alignSelf: 'flex-end',
    marginBottom: 0,
    maxWidth: '100%',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

const racksPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {},
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

export const Racks: React.FC<RacksProps> = props =>
  isRacksPersistedProps(props) ? (
    <RacksPersistedLayout {...props} />
  ) : (
    <RacksInitialLayout {...props} />
  );

Racks.displayName = 'Racks';

/** @deprecated Test / caption suites — create flow only */
export const RacksStep = RacksInitialLayout;
/** @deprecated Test / caption suites — opened request branch */
export const RacksInRequest = RacksPersistedLayout;
